import React from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';

import { Button, Select, Text, TextField } from 'components';
import { formatPrice } from 'core/shared';
import { useTranslations } from 'hooks/useTranslations';

import { CustomString } from 'config/translations';
import { UseQuery } from 'useQuery';
import { InvoiceLineCategory, InvoiceStatus } from 'useQuery/types';

const NONE_VAT = '__none__';

type FrequencyMode = '1' | '12' | 'custom';

function frequencyModeFromMonths(months: number): FrequencyMode {
  if (months === 1) {
    return '1';
  }
  if (months === 12) {
    return '12';
  }
  return 'custom';
}

function useFrequencyMode(months: number) {
  const [isCustom, setIsCustom] = React.useState(
    () => frequencyModeFromMonths(months) === 'custom',
  );

  React.useEffect(() => {
    if (frequencyModeFromMonths(months) === 'custom') {
      setIsCustom(true);
    }
  }, [months]);

  const mode: FrequencyMode = isCustom ? 'custom' : frequencyModeFromMonths(months);

  return { mode, setIsCustom };
}

// ---------------------------------------------------------------------------
// Billing frequency control (Monthly / Yearly / Custom N months)
// ---------------------------------------------------------------------------

interface FrequencyFieldProps {
  months: number;
  onChange: (months: number) => void;
  disabled?: boolean;
}

function FrequencyField({ months, onChange, disabled }: FrequencyFieldProps) {
  const translations = useTranslations('organisationInvoicing');

  const { mode, setIsCustom } = useFrequencyMode(months);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ width: 220 }}>
        <Select
          mode="single"
          label={translations.billingFrequency}
          value={mode}
          options={[
            { id: '1', label: translations.monthly },
            { id: '12', label: translations.yearly },
            { id: 'custom', label: translations.custom },
          ]}
          onChange={(value) => {
            if (value === 'custom') {
              setIsCustom(true);
              // Any non-1/12 value; 3 (quarterly) is the common custom cycle.
              onChange(months === 1 || months === 12 ? 3 : months);
              return;
            }
            setIsCustom(false);
            onChange(Number(value));
          }}
          disabled={disabled}
          fullWidth
        />
      </Box>
      {mode === 'custom' && (
        <Box sx={{ width: 170 }}>
          <TextField
            type="number"
            label={translations.customMonths}
            value={months}
            onChange={(value) => onChange(Math.max(1, Math.round(value)))}
            disabled={disabled}
            fullWidth
          />
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Per-section footer: "Unsaved changes" pill + Save button
// ---------------------------------------------------------------------------

interface SectionSaveFooterProps {
  dirty: boolean;
  saving: boolean;
  disabled?: boolean;
  error?: CustomString;
  onSave: () => void;
}

function SectionSaveFooter({ dirty, saving, disabled, error, onSave }: SectionSaveFooterProps) {
  const translations = useTranslations('organisationInvoicing');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        paddingTop: 2,
        marginTop: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {!!error && dirty && <Text.Small color="error.main">{error}</Text.Small>}
      {dirty && !error && (
        <Box
          sx={{
            paddingX: 1.5,
            paddingY: 0.5,
            borderRadius: 4,
            backgroundColor: 'warning.light',
          }}
        >
          <Text.Small color="warning.dark">{translations.unsavedChanges}</Text.Small>
        </Box>
      )}
      <Button
        label={translations.save}
        onClick={onSave}
        disabled={!dirty || saving || !!disabled}
        loading={saving}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Invoice status pill
// ---------------------------------------------------------------------------

// A draft was generated but never accepted by Exact, so it is neither a
// projection nor a fact: it still changes on the next run.
function useInvoiceStatusStyle(status: InvoiceStatus) {
  const translations = useTranslations('organisationInvoicing');

  const styles = {
    scheduled: {
      label: translations.statusScheduled,
      background: 'info.light',
      text: 'info.dark',
    },
    draft: { label: translations.statusDraft, background: 'warning.light', text: 'warning.dark' },
    invoiced: {
      label: translations.statusInvoiced,
      background: 'success.light',
      text: 'success.dark',
    },
  } as const;

  return styles[status] ?? styles.invoiced;
}

function InvoiceStatusChip({ status }: { status: InvoiceStatus }) {
  const style = useInvoiceStatusStyle(status);

  return (
    <Box
      sx={{
        display: 'inline-block',
        paddingX: 1.5,
        paddingY: 0.25,
        borderRadius: 4,
        backgroundColor: style.background,
      }}
    >
      <Text.Small color={style.text}>{style.label}</Text.Small>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Invoice line categories
// ---------------------------------------------------------------------------

function useInvoiceCategoryLabel() {
  const translations = useTranslations('organisationInvoicing');

  const labels: Record<InvoiceLineCategory, CustomString> = {
    subscription: translations.categorySubscription,
    discount: translations.categoryDiscount,
    usage: translations.categoryUsage,
    one_off: translations.categoryOneOff,
    passthrough: translations.categoryPassthrough,
  };

  // Unknown categories may arrive from the backend: fall back to the raw value.
  return (category: InvoiceLineCategory) => labels[category] ?? (category as CustomString);
}

// ---------------------------------------------------------------------------
// VAT options for dropdowns
// ---------------------------------------------------------------------------

function useVatOptions() {
  const translations = useTranslations('organisationInvoicing');
  const { data: vatData } = UseQuery.VAT();

  return React.useMemo(
    () => [
      { id: NONE_VAT, label: translations.vatDefault },
      ...(vatData?.result ?? [])
        .filter((vat): vat is NonNullable<typeof vat> => !!vat)
        .map((vat) => ({
          id: vat.id,
          label: `${vat.name} (${vat.percentage}%)` as CustomString,
        })),
    ],
    [vatData, translations],
  );
}

// Preselection for VAT dropdowns that are still unconfigured: the high rate
// (21%) covers nearly every organisation. NONE_VAT while the list is loading or
// when the backend serves no HIGH entry.
function useDefaultVatId() {
  const { data: vatData } = UseQuery.VAT();

  return React.useMemo(
    () => (vatData?.result ?? []).find((vat) => vat?.type === 'HIGH')?.id ?? NONE_VAT,
    [vatData],
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers (amounts are integer cents, dates ISO YYYY-MM-DD)
// ---------------------------------------------------------------------------

function formatSignedPrice(cents: number): string {
  const formatted = formatPrice({ price: Math.abs(cents), symbol: true });
  return cents < 0 ? `- ${formatted}` : formatted;
}

function formatMonth(isoDate: string): string {
  return dayjs(isoDate).format('MMM YYYY');
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start) {
    return '';
  }
  const from = formatMonth(start);
  const to = end ? formatMonth(end) : from;
  return from === to ? from : `${from} – ${to}`;
}

export {
  formatMonth,
  formatPeriod,
  formatSignedPrice,
  FrequencyField,
  type FrequencyMode,
  frequencyModeFromMonths,
  InvoiceStatusChip,
  NONE_VAT,
  SectionSaveFooter,
  useDefaultVatId,
  useFrequencyMode,
  useInvoiceCategoryLabel,
  useVatOptions,
};
